import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { formatDate, returnSign } from 'helpers/helpers'
import { httpOk, GET } from 'helpers/api'

// Журнал смены статусов точек. Один и тот же экран у админа и у партнёра —
// отличается только адрес API: партнёру бэк отдаёт только его точки.
function PosStatusHistory({ apiUrl = '/admin/pos-status-history' }) {
	const { t } = useTranslation()
	const location = useLocation()
	const date = new Date()

	const STATUS_OPTIONS = [
		{ 'id': '1', 'name': t('activated') },
		{ 'id': '-1', 'name': t('disconnected') },
		{ 'id': '-3', 'name': t('deleted') },
	]

	const [data, setData] = useState({ data: [], total: 0 })
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const {
		filterData,
		setFilterData,
		filterDataExist,
		setFilterDataExist,
		pagination,
		setPagination,
		applySearch,
		applyFilters,
		paginate,
		resetToInitialFilters,
	} = useFilterSearchPagination({
		initialFilterData: {
			pos_id: null,
			// по умолчанию — удаления за текущий месяц
			status: '-3',
			search: '',
			fast_date_id: 'currentMonth',
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(date.getFullYear(), date.getMonth() + 1, 0), 'yyyy-MM-dd'),
			page: null,
		},
		initialPagination: {
			url: apiUrl,
			response: null,
		},
	})

	async function searchWithFilter(nextFilterData = filterData) {
		var filterDataCopy = { ...nextFilterData }
		var filterUrl = apiUrl
		var urlParams = ""
		checkFilter(filterDataCopy)

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.status !== '' && filterDataCopy.status !== null)
			urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		if (`${apiUrl}${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	function checkFilter(currentFilterData) {
		if (
			!currentFilterData.pos_id &&
			!currentFilterData.search &&
			!currentFilterData.status &&
			!currentFilterData.start_date &&
			!currentFilterData.end_date &&
			!currentFilterData.page
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function handlePaginate(page) {
		paginate(page)
		searchWithFilter({ ...filterData, page })
	}

	function handleResetFilters() {
		resetToInitialFilters((nextFilterData) => {
			searchWithFilter(nextFilterData)
		})
	}

	function returnClassStatus(status) {
		if (status === null || status === undefined || status === '') return ""
		if (Number(status) === 1) return "bg-primary text-white"
		if (Number(status) === -1) return "bg-danger text-white"
		if (Number(status) === -2) return "bg-secondary text-white"
		if (Number(status) === -3) return "bg-danger text-white"
		return ""
	}

	function getStatusName(status) {
		if (status === null || status === undefined || status === '') return '—'
		if (Number(status) === 1) return t('activated')
		if (Number(status) === -1) return t('disconnected')
		if (Number(status) === -2) return t('frozen')
		if (Number(status) === -3) return t('deleted')
		return status
	}

	function getSourceName(source) {
		if (source === 'admin') return t('source_admin')
		if (source === 'partner') return t('source_partner')
		if (source === 'billing') return t('source_billing')
		return source || '—'
	}

	useEffect(() => {
		// со страницы точек приходим уже отфильтрованными по одной точке
		const posId = new URLSearchParams(location.search).get('pos_id')
		const nextFilterData = posId ? { ...filterData, 'pos_id': posId } : filterData

		if (posId) setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="vertical-center">
					<h4>{t('pos_status_history')} [{data.total ?? 0}]</h4>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									filterData={filterData}
									onSearchChange={(search) => applySearch(search, (nextFilterData) => {
										searchWithFilter(nextFilterData)
									})}>
								</FilterSearch>
							</div>
							<div className="d-flex gap-2">
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen} className="h-fit-content">
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>
									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('status')}</label>
												<Select
													isClearable
													options={STATUS_OPTIONS}
													value={STATUS_OPTIONS.find(option => option.id === (filterData.status ?? '')) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'status': option ? option.id : '' })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period, presetId) => setFilterData({ ...filterData, ...period, 'fast_date_id': presetId })}
													withTime={false}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => applyFilters((nextFilterData) => {
															searchWithFilter(nextFilterData)
														})}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							{filterDataExist &&
								<div className="d-flex gap-2">
									<div className="vertical-center">
										<button className="btn btn-outline-primary"
											onClick={() => handleResetFilters()}>
											{t('reset')}
										</button>
									</div>
								</div>
							}
						</div>
					</div>

					<div className="table-responsive" style={{ 'minHeight': '500px' }}>
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">ID</th>
									<th>{t('pos')}</th>
									<th className="text-center text-nowrap" style={{ 'minWidth': '100px' }}>{t('status_before')}</th>
									<th className="text-center text-nowrap" style={{ 'minWidth': '100px' }}>{t('status_after')}</th>
									<th style={{ 'minWidth': '220px' }}>{t('note')}</th>
									<th className="text-center text-nowrap">{t('source')}</th>
									<th className="text-nowrap">{t('author')}</th>
									<th className="text-center text-nowrap">{t('date')}</th>
								</tr>
							</thead>
							<tbody>
								{data.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.pos_id}</td>
										<td>{item.pos_name}</td>
										<td className={`text-center text-nowrap ${returnClassStatus(item.status_before)}`}>
											{getStatusName(item.status_before)}
										</td>
										<td className={`text-center text-nowrap ${returnClassStatus(item.status_after)}`}>
											{getStatusName(item.status_after)}
										</td>
										<td>{item.note || '—'}</td>
										<td className="text-center text-nowrap">{getSourceName(item.source)}</td>
										<td className="text-nowrap">{item.created_by_name || item.created_by || '—'}</td>
										<td className="text-center text-nowrap">{formatDate(item.created_at, 'dd.MM.yyyy HH:mm')}</td>
									</tr>
								))}
								{data.data?.length === 0 &&
									<tr>
										<td colSpan="8" className="text-center text-muted">{t('list_empty')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={handlePaginate}>
						</FilterPagination>
					}
				</div>
			</div>
		</>
	)
}

export default PosStatusHistory
